/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css } from 'lit';

/**
 * Toast CSS
 */
export const styles = css`
  :host {
    display: contents;
    -webkit-font-smoothing: var(--nile-webkit-font-smoothing, var(--ng-webkit-font-smoothing));
    -moz-osx-font-smoothing: var(--nile-moz-osx-font-smoothing, var(--ng-moz-osx-font-smoothing));
    text-rendering: var(--nile-text-rendering, var(--ng-text-rendering));

    /* For better DX, we'll reset the margin here so the base part can inherit it */
    margin: var(--nile-spacing-none);
  }

  .alert {
    max-width: 100%;
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: flex-start;
    border: solid 2px;
    border-radius: var(--nile-radius-md, var(--ng-radius-xl));
    margin: inherit;
    padding: var(--nile-spacing-xl, var(--ng-spacing-xl));
    gap: var(--nile-spacing-xl, var(--ng-spacing-xl));
    width: max-content;
    min-width: 400px;
  }

  .alert:not(.alert--has-icon) .alert__icon,
  .alert:not(.alert--closable) .alert__close-button {
    display: none;
  }

  .alert__prefix-icon-container {
    background-color: var(--nile-colors-red-500);
    border-radius: var(--nile-radius-sm);
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .alert__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    font-size: var(--nile-type-scale-1);
  }

  .alert--success {
    border: 1px solid var(--nile-toast-color-border-success, var(--ng-colors-border-secondary-alt));
    background: var(--nile-toast-color-background-success, var(--ng-colors-bg-primary-alt));
    box-shadow: var(--nile-toast-color-box-shadow, var(--ng-shadow-xs));
  }

  .alert--success .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-success, var(--ng-colors-bg-primary-alt));
  }

  .alert--info {
    border: 1px solid var(--nile-toast-color-border-info, var(--ng-colors-border-secondary-alt));
    background: var(--nile-toast-color-background-info, var(--ng-colors-bg-primary-alt));
    box-shadow: var(--nile-toast-color-box-shadow, var(--ng-shadow-xs));
  }

  .alert--info .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-info, var(--ng-colors-bg-primary-alt));
  }

  .alert--warning {
    border: 1px solid var(--nile-toast-color-border-warning, var(--ng-colors-border-secondary-alt));
    background: var(--nile-toast-color-background-warning, var(--ng-colors-bg-primary-alt));
    box-shadow: var(--nile-toast-color-box-shadow, var(--ng-shadow-xs));
  }

  .alert--warning .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-warning, var(--ng-colors-bg-primary-alt));
  }

  .alert--error {
    border: 1px solid var(--nile-toast-color-border-error, var(--ng-colors-border-secondary-alt));
    background: var(--nile-toast-color-background-error, var(--ng-colors-bg-primary-alt));
    box-shadow: var(--nile-toast-color-box-shadow, var(--ng-shadow-xs));
  }

  .alert--error .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-error, var(--ng-colors-bg-primary-alt));
  }

  .alert--gray {
    border: 1px solid var(--nile-toast-color-border-gray, var(--ng-colors-border-secondary-alt));
    background: var(--nile-toast-color-background-gray, var(--ng-colors-bg-primary-alt));
    box-shadow: var(--nile-toast-color-box-shadow, var(--ng-shadow-xs));
  }

  .alert--gray .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-gray, var(--ng-colors-bg-primary-alt));
  }

  .alert--black {
    border: 1px solid var(--nile-toast-color-border-black);
    background: var(--nile-toast-color-background-black);
    box-shadow: var(--nile-toast-color-box-black);
  }

  .alert--black .alert__prefix-icon-container {
    background: var(--nile-toast-color-icon-background-color-black);
  }

  .alert__message {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    align-items: center;
    flex-direction: column;
    align-items: flex-start;
    font-family: var(--nile-font-family-serif, var(--ng-font-family-display));
    font-size: var(--nile-type-scale-3);
    color: var(--nile-colors-dark-900);
    gap: var(--nile-spacing-sm, var(--ng-spacing-sm));
  }

  .alert__message--title {
    font-weight: var(--nile-font-weight-semi-bold, var(--ng-font-weight-semiboldibold));
    color:var(--nile-colors-dark-900, var(--ng-colors-text-secondary-700));
  }

  .alert__message--content {
    font-weight:var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color:var(--nile-colors-dark-500, var(--ng-colors-text-tertiary-600));
  }

  .alert__message--content-only {
  }

  .alert__close-button {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  .alert__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--nile-spacing-lg, var(--ng-spacing-lg));
    margin-top: var(--nile-spacing-xs, var(--ng-spacing-xs));
  }

  .alert__tag {
    height: 24px;
    display: flex;
    border-radius: var(--nile-radius-sm, var(--ng-radius-sm));
    border: 1px solid var(--nile-colors-neutral-400);
    background: var(--nile-colors-white-base);
  }

  .alert__tag-content {
    border-left: 1px solid var(--nile-colors-neutral-400);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--nile-spacing-none, var(--ng-spacing-none)) var(--nile-spacing-sm, var(--ng-spacing-sm));

    color: var(--nile-colors-dark-900, var(--ng-colors-text-tertiary-600));
    font-family:  var(--nile-font-family-serif, var(--ng-font-family-display));
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-style: normal;
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-semibold));
    line-height: var(--nile-line-height-xsmall, var(--ng-line-height-text-sm));
    letter-spacing: 0.2px;
  }

  .alert__tag-image {
    height: 24px;
    width: 24px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs)) var(--nile-radius-radius-none, var(--ng-radius-none)) var(--nile-radius-radius-none, var(--ng-radius-none)) var(--nile-radius-radius-xs, var(--ng-radius-xs));
  }

  .alert__prefix-img {
    height: 24px;
    border-radius: var(--nile-radius-sm, var(--ng-radius-sm));
  }

  .alert--no-content{
    align-items:center ;
  }

  [hidden] {
    display: none;
  }
`;

export default [styles];
